import React, { FC, MouseEvent, ReactNode, useEffect, useState } from 'react';

import { DateTime, DateTimeFormatOptions } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';

import { Star } from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { BaseStyle, CoreUtils, ShortCodeEnums, CoreCommonMessages, TimeZoneEnums } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { CountryCode } from '~/core/enums';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { parseValueFromLocalizedAttribute } from '~/core/utils';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';
import { useGetWebStreamURLQuery } from '~/redux/ducks/webStreamURL';
import {
    ActionPopover,
    CardActionContainer,
    CardDescription,
    CardIcon,
    CardLinkItem,
} from '~/views/pages/dotcom/schedule/styles';
import { ScheduleGame } from '~/views/pages/dotcom/schedule/types';

import { NeutralSiteIndicator } from '../../../../../neutralSiteIndicator/NeutralSiteIndicator';
import SpecialEventMatchup from '../../../../../specialEventMatchup/SpecialEventMatchup';
import { formatGameResult } from '../day/utils';
import { getPopupLinks } from '../utils';

import {
    AdditionalGamesContainer,
    EventCardItem,
    MatchupInfoContainer,
    MobileDayContainer,
    MobileDayOfMonth,
    MobileGameInfo,
    MobileTeamDisplay,
    MobileTopBar,
    PopperContent,
    SpecialEventImage,
    SpecialEventInformation,
} from './styles';

type Props = {
    gameList: Array<ScheduleGame>;
    dayOfMonth: string;
    team: ShortCodeEnums.TeamTriCode;
    timezone: TimeZoneEnums;
};

export const MobileDay: FC<Props> = ({ gameList, dayOfMonth, team, timezone }) => {
    const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
    const [specialEventAnchorElement, setSpecialEventAnchorElement] = useState<HTMLButtonElement | null>(null);
    const [openSelectedGameLinks, setOpenSelectedGameLinks] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedGameIndex, setSelectedGameIndex] = useState(0);

    const intl = useIntl();
    const isFrench = intl.locale === ShortCodeEnums.Language.French;
    const isSingleGameDay = gameList.length === 1;
    const selectedGame = gameList[selectedGameIndex];

    const { data: countryCode = defaultLocationData } = useLocationQuery({});

    const { data: streamUrl } = useGetWebStreamURLQuery(
        {
            broadcasts: selectedGame?.tvBroadcasts || [],
            countryCode,
            language: intl.locale as ShortCodeEnums.Language,
        },
        { skip: countryCode === CountryCode.None }
    );

    const threeMinuteRecap = useOptionForLocale({
        [ShortCodeEnums.Language.English]: selectedGame?.threeMinRecap,
        [ShortCodeEnums.Language.French]: selectedGame?.threeMinRecapFr,
    });

    const ticketsLink = useOptionForLocale({
        [ShortCodeEnums.Language.English]: selectedGame?.ticketsLink,
        [ShortCodeEnums.Language.French]: selectedGame?.ticketsLinkFr,
    });

    const gameLinks = getPopupLinks(selectedGame, team, ticketsLink, streamUrl, threeMinuteRecap);
    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (gameLinks.length > 0) {
            setAnchorElement(event.currentTarget);
            setIsOpen(true);
        }
    };

    const handleClose = () => {
        setAnchorElement(null);
        setIsOpen(false);
    };

    const multiGameHandleClick = (index: number) => {
        setSelectedGameIndex(index);
        if (gameLinks.length > 0) {
            setOpenSelectedGameLinks(true);
        }
    };

    const multiGameHandleClose = () => {
        handleClose();

        /* We set a timeout here so that the value switches after the popup closing transition completes. This prevents
         * the popup display from switching back quickly to the multiple games view, and provides us with a cleaner
         * transition.
         */
        setTimeout(() => {
            setOpenSelectedGameLinks(false);
        }, 500);
    };

    useEffect(() => {
        const resizeListener = () => {
            setAnchorElement(null);
            setSpecialEventAnchorElement(null);
        };
        if (anchorElement || specialEventAnchorElement) {
            window.addEventListener('resize', resizeListener);
        }
        return () => window.removeEventListener('resize', resizeListener);
    }, [anchorElement, specialEventAnchorElement]);

    const id = isOpen ? 'simple-popover' : undefined;

    const formatSpecialGame = (game: ScheduleGame): ReactNode =>
        game.homeTeam.homeSplitSquad || game.awayTeam.awaySplitSquad ? (
            <FormattedMessage {...CommonMessages.gameTypeDescriptions.long.splitSquad} />
        ) : (
            game.venue.default
        );

    const getTimeZone = (game: ScheduleGame, timezone: TimeZoneEnums): string =>
        timezone === TimeZoneEnums.VENUE ? game.venueTimezone : timezone;

    const getDateTimeFormat = (minute: number): DateTimeFormatOptions =>
        minute === 0 ? { hour: 'numeric' } : { hour: 'numeric', minute: 'numeric' };

    const formatTime = (game: ScheduleGame): string => {
        const time = DateTime.fromISO(game.startTimeUTC);
        const zone = getTimeZone(game, timezone);
        const dateTimeFormat = getDateTimeFormat(time.minute);

        return time.setZone(zone).toLocaleString(dateTimeFormat);
    };

    const formatGameInformation = (selectedGame: ScheduleGame): ReactNode => {
        const timeString = selectedGame.startTimeUTC && formatTime(selectedGame);

        if (CoreUtils.isPregame(selectedGame.gameState)) {
            return (
                selectedGame &&
                (selectedGame.gameScheduleState === ShortCodeEnums.GameScheduleState.scheduled ? (
                    <>{selectedGame.startTimeUTC && timeString}</>
                ) : (
                    <CoreUtils.SafeFormattedMessage
                        message={
                            CoreCommonMessages.gameScheduleStateDescriptions.abbreviations[
                                selectedGame.gameScheduleState
                            ]
                        }
                    />
                ))
            );
        } else if (CoreUtils.isGameLive(selectedGame.gameState)) {
            return (
                <FormattedMessage {...CoreCommonMessages.gameStateDescriptions.long[ShortCodeEnums.GameState.live]} />
            );
        }

        return formatGameResult(
            selectedGame.awayTeam.abbrev,
            selectedGame.homeTeam.abbrev,
            team,
            selectedGame.awayTeam.score,
            selectedGame.homeTeam.score
        );
    };

    return selectedGame ? (
        <>
            <MobileDayContainer onClick={handleClick}>
                <MobileTopBar>
                    <MobileDayOfMonth>{dayOfMonth}</MobileDayOfMonth>
                    {selectedGame.specialEvent && <Star />}
                </MobileTopBar>
                <MobileTeamDisplay>
                    {selectedGame && selectedGame?.homeTeam.abbrev === team
                        ? selectedGame.awayTeam.abbrev
                        : selectedGame.homeTeam.abbrev}
                    {!isSingleGameDay && (
                        <AdditionalGamesContainer>
                            <FormattedMessage
                                description="A message displaying that there are an additional number of games on a set day"
                                defaultMessage="+{number}"
                                values={{
                                    number: gameList.length - 1,
                                }}
                            />
                        </AdditionalGamesContainer>
                    )}
                </MobileTeamDisplay>
                <MobileGameInfo $isLive={CoreUtils.isGameLive(selectedGame.gameState)} $isFrench={isFrench}>
                    {formatGameInformation(selectedGame)}
                </MobileGameInfo>
            </MobileDayContainer>
            {isSingleGameDay && (
                <ActionPopover
                    id={id}
                    open={isOpen}
                    anchorEl={anchorElement}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                >
                    <BaseStyle.RootStyle>
                        <PopperContent>
                            {selectedGame?.specialEvent ? (
                                <MatchupInfoContainer>
                                    {selectedGame.specialEvent?.lightLogoUrl ? (
                                        <SpecialEventImage
                                            src={parseValueFromLocalizedAttribute(
                                                selectedGame.specialEvent.lightLogoUrl,
                                                intl.locale
                                            )}
                                        />
                                    ) : (
                                        <SpecialEventMatchup
                                            intl={intl}
                                            game={selectedGame}
                                            specialEvent={selectedGame.specialEvent}
                                            isCalendarView
                                            allowTruncatedText={false}
                                        />
                                    )}
                                    {selectedGame.neutralSite && <NeutralSiteIndicator />}
                                </MatchupInfoContainer>
                            ) : (
                                selectedGame.neutralSite && (
                                    <MatchupInfoContainer>
                                        <NeutralSiteIndicator />
                                    </MatchupInfoContainer>
                                )
                            )}
                            {gameLinks.map((gameLink) => (
                                <CardLinkItem key={gameLink.actionUrl} href={gameLink.actionUrl} target="_blank">
                                    <CardIcon>{gameLink.icon}</CardIcon>
                                    <CardDescription>{gameLink.message}</CardDescription>
                                    <CardActionContainer>
                                        <ChevronRightIcon />
                                    </CardActionContainer>
                                </CardLinkItem>
                            ))}
                        </PopperContent>
                    </BaseStyle.RootStyle>
                </ActionPopover>
            )}
            {!isSingleGameDay && (
                <ActionPopover
                    id={id}
                    open={isOpen}
                    anchorEl={anchorElement}
                    onClose={multiGameHandleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                >
                    <BaseStyle.RootStyle>
                        <PopperContent>
                            {openSelectedGameLinks
                                ? gameLinks.map((gameLink) => (
                                      <CardLinkItem key={gameLink.actionUrl} href={gameLink.actionUrl} target="_blank">
                                          <CardIcon>{gameLink.icon && gameLink.icon}</CardIcon>
                                          <CardDescription>{gameLink.message}</CardDescription>
                                          <CardActionContainer>
                                              <ChevronRightIcon />
                                          </CardActionContainer>
                                      </CardLinkItem>
                                  ))
                                : gameList.map((game, index) => (
                                      <EventCardItem key={index} onClick={() => multiGameHandleClick(index)}>
                                          <SpecialEventInformation>
                                              <CardDescription>
                                                  {game.specialEvent?.name
                                                      ? parseValueFromLocalizedAttribute(game.specialEvent.name)
                                                      : formatSpecialGame(game)}
                                              </CardDescription>
                                              <FormattedMessage
                                                  description="A message displaying the opponent and time of game"
                                                  defaultMessage="{opponent} - {time}"
                                                  values={{
                                                      opponent:
                                                          team === game.homeTeam.abbrev
                                                              ? game.awayTeam.abbrev
                                                              : game.homeTeam.abbrev,
                                                      time: game.startTimeUTC && formatTime(game),
                                                  }}
                                              />
                                              {selectedGame.neutralSite && <NeutralSiteIndicator />}
                                          </SpecialEventInformation>
                                          <CardActionContainer>
                                              <ChevronRightIcon />
                                          </CardActionContainer>
                                      </EventCardItem>
                                  ))}
                        </PopperContent>
                    </BaseStyle.RootStyle>
                </ActionPopover>
            )}
        </>
    ) : null;
};

export default MobileDay;
